import { useState } from "react";
import { ChevronDown } from "lucide-react";

import type { Alert } from "../types/alerts";
import { formatAlertWindow } from "../lib/format";
import AlertSeverityIcon from "./AlertSeverityIcon";

const SEVERITY_STYLES: Record<Alert["severity"], { border: string; bg: string; text: string }> = {
  extreme: { border: "border-error", bg: "bg-[#fff7f7]", text: "text-error" },
  severe: { border: "border-error", bg: "bg-[#fff7f7]", text: "text-error" },
  moderate: {
    border: "border-alert-moderate",
    bg: "bg-alert-moderate-container",
    text: "text-alert-moderate",
  },
  minor: {
    border: "border-outline",
    bg: "bg-surface-container",
    text: "text-on-surface-variant",
  },
  unknown: {
    border: "border-outline",
    bg: "bg-surface-container",
    text: "text-on-surface-variant",
  },
};

interface Props {
  alert: Alert;
}

export default function AlertCard({ alert }: Props) {
  const [expanded, setExpanded] = useState(false);
  const styles = SEVERITY_STYLES[alert.severity];
  const detailId = `alert-detail-${alert.id}`;
  const hasDetail = alert.description || alert.instruction;

  return (
    <li className={`rounded-lg border px-4 py-3 ${styles.border} ${styles.bg}`}>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={detailId}
        onClick={() => setExpanded((prev) => !prev)}
        disabled={!hasDetail}
        className="flex w-full items-start gap-3 text-left"
      >
        <span className="mt-0.5 shrink-0">
          <AlertSeverityIcon severity={alert.severity} size={20} />
        </span>

        <div className="min-w-0 flex-1">
          <span
            className={`block text-label-small font-semibold uppercase tracking-wide ${styles.text}`}
          >
            {alert.severity}
          </span>
          <span className="block text-title-medium font-medium text-on-surface">{alert.event}</span>
          {alert.headline ? (
            <span className="mt-0.5 block text-body-medium text-on-surface-variant">
              {alert.headline}
            </span>
          ) : null}
          <span className="mt-1 block text-label-medium text-on-surface-variant">
            {formatAlertWindow(alert.effectiveDisplay, alert.expiresDisplay)}
          </span>
        </div>

        {hasDetail ? (
          <ChevronDown
            size={20}
            aria-hidden
            className={`mt-0.5 shrink-0 text-on-surface-variant transition-transform duration-200 ${
              expanded ? "rotate-180" : ""
            }`}
          />
        ) : null}
      </button>

      {expanded && hasDetail ? (
        <div
          id={detailId}
          role="region"
          aria-label={`Details for ${alert.event}`}
          className={`mt-3 border-t pt-3 ${styles.border}`}
        >
          {alert.description ? (
            <p className="whitespace-pre-wrap text-body-medium text-on-surface">
              {alert.description}
            </p>
          ) : null}

          {alert.instruction ? (
            <div className="mt-3">
              <h4 className="mb-1 text-label-large font-semibold text-on-surface">What to do</h4>
              <p className="whitespace-pre-wrap text-body-medium text-on-surface">
                {alert.instruction}
              </p>
            </div>
          ) : null}

          {alert.senderName ? (
            <p className="mt-3 text-label-medium text-on-surface-variant">
              Issued by {alert.senderName}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
